import React, { useState, useEffect, useRef } from 'react';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { TextField, Box, Popover, Stack, Button, IconButton } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { getIn } from 'formik';
import { format, setHours, setMinutes, isValid, startOfMinute } from 'date-fns';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';


const ScrollColumn = ({ items, selected, onSelect, flex = 1, rootRef, centered = false }) => (
  <Box 
    sx={{ 
      flex, 
      height: '100%', 
      position: 'relative', 
      minHeight: 0,
      minWidth: 45
    }}
  >
    <Box
      ref={rootRef}
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        overflowY: 'auto',
        textAlign: 'center',
        scrollbarWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: centered ? 'center' : 'initial',
        paddingTop: centered ? 0 : '10px',
        paddingBottom: centered ? 0 : '10px',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {items.map((item) => {
        const isSelected = selected.toString() === item.toString();
        return (
          <Box
            key={item}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
            className={isSelected ? 'selected-item' : ''}
            sx={{
              py: 0.6,
              cursor: 'pointer',
              fontSize: '0.82rem',
              fontWeight: isSelected ? 700 : 400,
              color: isSelected ? 'white' : 'text.primary',
              backgroundColor: isSelected ? 'primary.main' : 'transparent',
              borderRadius: '6px',
              mx: 0.4,
              mb: 0.3,
              minHeight: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: isSelected ? 'primary.main' : alpha('#1976d2', 0.08),
              }
            }}
          >
            {item}
          </Box>
        );
      })}
    </Box>
  </Box>
);

const TimeSpinner = ({ value, onChange, open, sessionKey }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  const date = value && isValid(new Date(value)) ? new Date(value) : startOfMinute(new Date());
  const currentHour = parseInt(format(date, 'h'));
  const currentMinute = format(date, 'mm');
  const currentPeriod = format(date, 'a');

  const hourRef = useRef(null);
  const minRef = useRef(null);
  const periodRef = useRef(null);
  const hasAlignedRef = useRef(null);

  useEffect(() => {
    if (open && hasAlignedRef.current !== sessionKey) {
      setTimeout(() => {
        if (hourRef.current) {
          const sel = hourRef.current.querySelector('.selected-item');
          if (sel) sel.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        if (minRef.current) {
          const sel = minRef.current.querySelector('.selected-item');
          if (sel) sel.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        hasAlignedRef.current = sessionKey;
      }, 50);
    }
  }, [open, sessionKey]);

  const handleTimeChange = (type, val) => {
    let newDate = new Date(date);
    if (type === 'hour') {
      const isPM = format(newDate, 'a') === 'PM';
      let h = parseInt(val);
      if (isPM && h < 12) h += 12;
      if (!isPM && h === 12) h = 0;
      newDate = setHours(newDate, h);
    } else if (type === 'minute') {
      newDate = setMinutes(newDate, parseInt(val));
    } else if (type === 'period') {
      let h = newDate.getHours();
      if (val === 'PM' && h < 12) h += 12;
      if (val === 'AM' && h >= 12) h -= 12;
      newDate = setHours(newDate, h);
    }
    onChange(newDate);
  };

  return (
    <Stack 
      direction="row" 
      sx={{ 
        flex: 'none',
        width: 165, 
        p: 0.5, 
        alignSelf: 'stretch',
        bgcolor: alpha('#000', 0.005), 
        borderLeft: '1px solid rgba(0,0,0,0.08)' ,
        minHeight: 0
      }}
    >
      <ScrollColumn rootRef={hourRef} items={hours} selected={currentHour} onSelect={(v) => handleTimeChange('hour', v)} flex={1} />
      <ScrollColumn rootRef={minRef} items={minutes} selected={currentMinute} onSelect={(v) => handleTimeChange('minute', v)} flex={1} />
      <ScrollColumn 
        rootRef={periodRef} 
        items={['AM', 'PM']} 
        selected={currentPeriod} 
        onSelect={(v) => handleTimeChange('period', v)} 
        flex={1} 
        centered={true}
      />
    </Stack>
  );
};



// mode: "datetime" (default) or "date"
export const DateTimePicker = ({ label, value, onChange, error, helperText, onFocus, onBlur, mode = 'datetime', minDate, maxDate, ...other }) => {
  const isDateOnly = mode === 'date';
  const displayFormat = isDateOnly ? 'MM/dd/yyyy' : 'MM/dd/yyyy hh:mm a';
  const placeholder = isDateOnly ? 'mm/dd/yyyy' : 'mm/dd/yyyy --:-- --';

  const [anchorEl, setAnchorEl] = useState(null);
  const containerRef = useRef(null);
  const instanceIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const instanceId = instanceIdRef.current;
  const [inputValue, setInputValue] = useState('');
  const isRecentlyClosedRef = useRef(false);
  const closedTimeoutRef = useRef(null);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    if (value && isValid(new Date(value))) {
      setInputValue(format(new Date(value), displayFormat));
    } else {
      setInputValue('');
    }
  }, [value, displayFormat]);

  useEffect(() => {
    const handleOtherPickerOpen = (event) => {
      if (event.detail.id !== instanceId) setAnchorEl(null);
    };
    window.addEventListener('picker-opened', handleOtherPickerOpen);
    return () => {
      window.removeEventListener('picker-opened', handleOtherPickerOpen);
      if (closedTimeoutRef.current) clearTimeout(closedTimeoutRef.current);
    };
  }, [instanceId]);

  const handleOpen = () => {
    if (isRecentlyClosedRef.current) return;
    if (!anchorEl) {
      window.dispatchEvent(new CustomEvent('picker-opened', { detail: { id: instanceId } }));
      setAnchorEl(containerRef.current);
      setSessionKey(prev => prev + 1);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    isRecentlyClosedRef.current = true;
    if (closedTimeoutRef.current) clearTimeout(closedTimeoutRef.current);
    closedTimeoutRef.current = setTimeout(() => {
      isRecentlyClosedRef.current = false;
    }, 300);
  };

  const popoverWidth = isDateOnly ? 245 : 410;

  return (
    <Box sx={{ width: '100%' }} ref={containerRef}>
      {/* CSS injection for forced styles */}
      <style>{`
        .MuiPickersCalendarHeader-root {
          display: flex !important;
          position: relative !important;
          padding: 0px 16px 0 16px !important;
          align-items: center !important;
          min-height: 48px !important;
        }
        .MuiPickersCalendarHeader-label {
          font-size: 0.9rem !important;
          font-weight: 700 !important;
          white-space: nowrap !important;
          display: flex !important;
          align-items: center !important;
          color: #2c3e50 !important;
        }
        .MuiPickersCalendarHeader-labelContainer {
          flex: none !important;
          margin: 0 !important;
          display: flex !important;
          align-items: center !important;
          padding-right: 70px !important;
          overflow: visible !important;
        }
        .MuiPickersCalendarHeader-labelContainer button {
          padding: 2px !important;
          margin-left: 0px !important;
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }
        
        .MuiYearPicker-root {
          max-height: 230px !important; 
          padding: 8px !important;
          overflow-y: auto !important;
          scrollbar-width: none !important;
          display: grid !important;
          grid-template-columns: repeat(3, 1fr) !important;
        }
        .MuiYearPicker-root::-webkit-scrollbar {
          display: none !important;
        }
        .MuiPickersYear-yearButton {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          border-radius: 6px !important;
          font-size: 0.85rem !important;
          margin: 2px !important;
          height: 32px !important;
          min-height: 32px !important;
          width: auto !important;
          color: #2c3e50 !important;
        }
        .MuiPickersYear-yearButton.Mui-selected {
          background-color: #1976d2 !important;
          color: white !important;
          font-weight: 700 !important;
        }
        .MuiPickersYear-yearButton:focus, 
        .MuiPickersYear-yearButton:active {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        .MuiPickersArrowSwitcher-root {
          position: absolute !important;
          right: 12px !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 0 !important;
          width: 68px !important;
        }
        .MuiPickersArrowSwitcher-spacer {
          display: none !important;
          width: 0 !important;
        }
        .MuiPickersCalendarHeader-root button,
        .MuiPickersArrowSwitcher-root button {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 0 !important;
          margin: 0 !important;
          width: 34px !important;
          height: 34px !important;
          min-width: 0 !important;
          flex-shrink: 0 !important;
        }
        .MuiPickersArrowSwitcher-root button svg {
          font-size: 1.4rem !important;
        }
      `}</style>

      <TextField
        fullWidth
        label={label}
        InputLabelProps={{ shrink: true, ...(other.InputLabelProps || {}) }}
        value={inputValue}
        readOnly
        onFocus={(e) => { handleOpen(); if (onFocus) onFocus(e); }}
        onClick={handleOpen}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        autoComplete="off"
        inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
        InputProps={{
          endAdornment: (
            <IconButton size="small" onClick={handleOpen}>
              <CalendarMonthIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
            </IconButton>
          )
        }}
        {...other}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: popoverWidth,
            mt: 0.5,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0,0,0,0.05)',
            '& .MuiCalendarPicker-root': { 
                width: 245, 
                height: 'auto',
                minHeight: '230px', 
                padding: 0,
                margin: 0
            },
            '& .MuiPickersCalendarHeader-root': {
                marginTop: 0,
                marginBottom: 0
            },
            '& .MuiCalendarPicker-viewTransitionContainer': {
                padding: '0 8px',
                minHeight: '200px', 
                height: 'auto',
                overflow: 'hidden'
            },
            '& .PrivatePickersSlideTransition-root': {
                minHeight: '200px'
            },
            '& .MuiTypography-root': { fontSize: '0.85rem' },
            '& .MuiPickersDay-root': { 
                width: 32, 
                height: 32, 
                fontSize: '0.85rem', 
                margin: '1px'
            }
          }
        }}
      >
        <Stack direction="row" sx={{ flex: 1, overflow: 'hidden', alignItems: 'stretch' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CalendarPicker
              date={value && isValid(new Date(value)) ? new Date(value) : new Date()}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(newDate) => {
                if (isDateOnly) {
                  onChange(newDate);
                } else {
                  const current = value && isValid(new Date(value)) ? new Date(value) : new Date();
                  const combined = setHours(setMinutes(newDate, current.getMinutes()), current.getHours());
                  onChange(combined);
                }
              }}
            />
          </Box>
          {!isDateOnly && (
            <TimeSpinner 
              value={value} 
              onChange={onChange} 
              open={Boolean(anchorEl)}
              sessionKey={sessionKey}
            />
          )}
        </Stack>
        
        <Box sx={{ p: 0.5, px: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha('#1976d2', 0.02) }}>
          <Button size="small" sx={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 0, px: 1 }} onClick={() => onChange(null)}>Clear</Button>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button size="small" sx={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 0, px: 1 }} onClick={() => onChange(new Date())}>Today</Button>
            <Button 
                variant="contained" 
                size="small" 
                onClick={() => {
                    if (!value) onChange(new Date());
                    handleClose();
                }}
                sx={{ 
                    fontWeight: 700, 
                    textTransform: 'capitalize', 
                    borderRadius: '6px',
                    px: 1.5,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' }
                }}
            >
                OK
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export const FormikDateTimePicker = ({ name, formik, label, mode, minDate, maxDate, ...other }) => {
  const { values, setFieldValue, errors, touched } = formik;
  const value = getIn(values, name) || null;
  const error = getIn(errors, name);
  const isTouched = getIn(touched, name);
  const hasError = isTouched && Boolean(error);
  return (
    <DateTimePicker
      label={label}
      value={value}
      onChange={(newValue) => setFieldValue(name, newValue)}
      error={hasError}
      helperText={hasError ? error : ''}
      mode={mode}
      minDate={minDate}
      maxDate={maxDate}
      {...other}
    />
  );
};
